import { useMemo, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sparkles,
  Pencil,
  Save,
  X,
  AlertCircle,
  Braces,
  Info,
  Loader2,
} from 'lucide-react'
import { format } from 'date-fns'
import type { CreativeGenome } from '../types'
import {
  parseGenome,
  stringifyGenome,
  extractSwatches,
  type Swatch,
} from '../lib/genomeUtils'

interface CreativeGenomePanelProps {
  genome: CreativeGenome | null
  updatedAt: string | null
  isLoading?: boolean
  isSaving?: boolean
  onSave: (genome: CreativeGenome) => Promise<unknown>
}

const SCAFFOLD: CreativeGenome = {
  concept: '',
  tokens: { palette: {} },
  motifs: [],
  voice: { tone: '', sampleCopy: [], vocabulary: [] },
  framing: {},
  acquire: {},
  motion: {},
  lineage: {},
  version: '1',
  source: 'ams-admin',
}

const SCHEMA_HINT =
  'CreativeGenome (stored verbatim as jsonb): { concept, tokens (design tokens — palette/type), ' +
  'motifs: string[], voice: { tone, sampleCopy[], vocabulary[] }, framing, acquire, motion, ' +
  'lineage, version, source }. Any additional keys are preserved.'

export function CreativeGenomePanel({
  genome,
  updatedAt,
  isLoading,
  isSaving,
  onSave,
}: CreativeGenomePanelProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)

  const startEditing = (seed: CreativeGenome) => {
    setDraft(stringifyGenome(seed))
    setError(null)
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setError(null)
  }

  const handleFormat = () => {
    const result = parseGenome(draft)
    if (!result.ok) {
      setError(result.error)
      return
    }
    setDraft(stringifyGenome(result.value))
    setError(null)
  }

  const handleSave = async () => {
    const result = parseGenome(draft)
    if (!result.ok) {
      setError(result.error)
      return
    }
    setError(null)
    await onSave(result.value)
    setIsEditing(false)
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Creative Genome</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Creative Genome
            </CardTitle>
            <CardDescription className="mt-1 max-w-2xl">
              The design seed that drives this artist’s generated portfolio and Moments. AMS is the
              source of record — changes flow to the artist’s generated experiences.
            </CardDescription>
          </div>
          {!isEditing && (
            <Button
              variant={genome ? 'outline' : 'default'}
              size="sm"
              onClick={() => startEditing(genome ?? SCAFFOLD)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {genome ? 'Edit' : 'Create genome'}
            </Button>
          )}
        </div>
        {(genome?.version !== undefined || updatedAt) && !isEditing && (
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {genome?.version !== undefined && (
              <Badge variant="secondary">version {String(genome.version)}</Badge>
            )}
            {genome?.source && <Badge variant="outline">source: {genome.source}</Badge>}
            {updatedAt && (
              <span className="text-xs text-muted-foreground">
                Updated {format(new Date(updatedAt), 'MMM d, yyyy • h:mm a')}
              </span>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent>
        {isEditing ? (
          <GenomeEditor
            draft={draft}
            error={error}
            isSaving={isSaving}
            onChange={(v) => {
              setDraft(v)
              if (error) setError(null)
            }}
            onFormat={handleFormat}
            onCancel={cancelEditing}
            onSave={handleSave}
          />
        ) : genome ? (
          <GenomeView genome={genome} />
        ) : (
          <div className="flex flex-col items-center justify-center rounded-md border border-dashed py-12 text-center">
            <Sparkles className="mb-3 h-8 w-8 text-muted-foreground" />
            <p className="font-medium">No genome yet</p>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              This artist has no creative genome. Create one to seed their generated portfolio and
              Moments.
            </p>
            <Button className="mt-4" size="sm" onClick={() => startEditing(SCAFFOLD)}>
              <Pencil className="mr-2 h-4 w-4" />
              Create genome
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// =============================================================================
// View
// =============================================================================

function GenomeView({ genome }: { genome: CreativeGenome }) {
  const swatches = useMemo<Swatch[]>(() => extractSwatches(genome.tokens), [genome.tokens])
  const motifs = Array.isArray(genome.motifs) ? genome.motifs : []
  const voice = genome.voice

  return (
    <div className="space-y-6">
      {genome.concept && (
        <div>
          <SectionLabel>Concept</SectionLabel>
          <p className="text-lg font-serif leading-snug">{genome.concept}</p>
        </div>
      )}

      {swatches.length > 0 && (
        <div>
          <SectionLabel>Palette · Tokens</SectionLabel>
          <div className="flex flex-wrap gap-3">
            {swatches.map((s) => (
              <div key={s.label} className="flex items-center gap-2">
                <span
                  className="h-8 w-8 rounded-md border shadow-sm"
                  style={{ backgroundColor: s.value }}
                  title={`${s.label}: ${s.value}`}
                />
                <div className="leading-tight">
                  <div className="text-xs font-medium">{s.label}</div>
                  <div className="text-xs text-muted-foreground">{s.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {motifs.length > 0 && (
        <div>
          <SectionLabel>Motifs</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {motifs.map((m, i) => (
              <Badge key={`${m}-${i}`} variant="secondary">
                {String(m)}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {voice && (
        <div>
          <SectionLabel>Voice</SectionLabel>
          <div className="space-y-3">
            {voice.tone && (
              <p className="text-sm">
                <span className="text-muted-foreground">Tone: </span>
                {voice.tone}
              </p>
            )}
            {Array.isArray(voice.sampleCopy) && voice.sampleCopy.length > 0 && (
              <ul className="space-y-1">
                {voice.sampleCopy.map((c, i) => (
                  <li
                    key={i}
                    className="border-l-2 border-muted pl-3 text-sm italic text-muted-foreground"
                  >
                    “{String(c)}”
                  </li>
                ))}
              </ul>
            )}
            {Array.isArray(voice.vocabulary) && voice.vocabulary.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {voice.vocabulary.map((w, i) => (
                  <Badge key={`${w}-${i}`} variant="outline">
                    {String(w)}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Separator />

      <div className="grid gap-4 md:grid-cols-2">
        <ObjectSection title="Framing" value={genome.framing} />
        <ObjectSection title="Acquire" value={genome.acquire} />
        <ObjectSection title="Motion" value={genome.motion} />
        <ObjectSection title="Lineage" value={genome.lineage} />
      </div>

      {genome.tokens != null && (
        <ObjectSection title="Tokens (raw)" value={genome.tokens} />
      )}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </p>
  )
}

function ObjectSection({ title, value }: { title: string; value: unknown }) {
  if (value == null || (typeof value === 'object' && Object.keys(value).length === 0)) {
    return null
  }
  return (
    <div>
      <SectionLabel>{title}</SectionLabel>
      <pre className="max-h-64 overflow-auto rounded-md bg-muted p-3 text-xs">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}

// =============================================================================
// Editor
// =============================================================================

interface GenomeEditorProps {
  draft: string
  error: string | null
  isSaving?: boolean
  onChange: (value: string) => void
  onFormat: () => void
  onCancel: () => void
  onSave: () => void
}

function GenomeEditor({
  draft,
  error,
  isSaving,
  onChange,
  onFormat,
  onCancel,
  onSave,
}: GenomeEditorProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{SCHEMA_HINT}</span>
      </div>

      <Textarea
        value={draft}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        className="min-h-[420px] font-mono text-xs"
        aria-label="Creative Genome JSON editor"
      />

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={onSave} disabled={isSaving} size="sm">
          {isSaving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save genome
        </Button>
        <Button variant="outline" size="sm" onClick={onFormat} disabled={isSaving}>
          <Braces className="mr-2 h-4 w-4" />
          Format JSON
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
          <X className="mr-2 h-4 w-4" />
          Cancel
        </Button>
      </div>
    </div>
  )
}
